import { htmlToBlocks } from '../htmlToBlocks';
import { stripHtml } from '../caretUtils';
import { sanitizeClipboardHtml } from './sanitizeHtml';
import { applyInlineMarkdownToTree } from './inlineMarkdown';
import {
  CHECKBOX_LINE_RE,
  PIPE_ROW_RE,
  checkboxTickFromMarker,
  pipeLinesToTableBlock,
  textToBlocks,
} from './textToBlocks';
import { tickToProps } from '../tickOverrides';

/** Recursively clean each block's content down to safe inline HTML. */
const sanitizeBlockTree = (blocks) =>
  blocks.map((b) => {
    // Code blocks store raw text — never run HTML sanitization (it would
    // turn newlines into <br> and strip the indentation we need to keep).
    if (b.type === 'code') {
      return {
        ...b,
        children: b.children?.length ? sanitizeBlockTree(b.children) : b.children,
      };
    }
    const next = {
      ...b,
      content: b.content ? sanitizeClipboardHtml(b.content) : b.content,
      children: b.children?.length ? sanitizeBlockTree(b.children) : b.children,
    };
    if (b.type === 'table' && b.props?.tableData) {
      const { headers = [], rows = [] } = b.props.tableData;
      next.props = {
        ...b.props,
        tableData: {
          headers: headers.map((h) =>
            Array.isArray(h) ? h : sanitizeClipboardHtml(String(h ?? ''))
          ),
          rows: rows.map((row) =>
            (row || []).map((c) =>
              Array.isArray(c) ? c : sanitizeClipboardHtml(String(c ?? ''))
            )
          ),
        },
      };
    }
    return next;
  });

/**
 * Plain text that should be parsed as markdown structure rather than left
 * as literal markers inside HTML-derived text blocks. Most apps put BOTH
 * text/html and text/plain on the clipboard; without this, `[ ] task` lines
 * become plain paragraphs because the HTML path wins.
 *
 * TipTap task-list HTML (`data-type=taskList`) is excluded — that path
 * already produces real checkbox blocks and shouldn't be overridden.
 */
const textLooksLikeMarkdownStructure = (text, html) => {
  if (!text) return false;
  if (/(^|\n)\s*<aside/i.test(text)) return true;
  // Real task-list HTML already maps to checkboxes — keep that path
  if (html && /data-type=["']?taskList/i.test(html)) return false;
  return (
    /(?:^|\n)\s*```/.test(text) ||
    /(?:^|\n)\s*(?:[-*]\s+)?\[(?: |x|-)?\]\s+\S/i.test(text) ||
    /(?:^|\n)\s*#{1,6}\s+\S/.test(text) ||
    /(?:^|\n)\s*>\s+\S/.test(text) ||
    /(?:^|\n)\s*\|.+\|\s*(?:\n|$)/.test(text) ||
    /(?:^|\n)\s*-{3,}\s*(?:\n|$)/.test(text) ||
    /(?:^|\n)\s*[-*]\s+\S/.test(text) ||
    /(?:^|\n)\s*\d+\.\s+\S/.test(text)
  );
};

/**
 * Safety net for HTML-only (or HTML-preferred) pastes: turn text blocks whose
 * visible content still starts with a `[ ]` / `[x]` marker into checkboxes.
 */
const promoteCheckboxMarkers = (blocks) =>
  blocks.map((b) => {
    const children = b.children?.length ? promoteCheckboxMarkers(b.children) : b.children;
    if (b.type !== 'text') return children !== b.children ? { ...b, children } : b;
    const plain = stripHtml(b.content || '').replace(/\u00a0/g, ' ').trim();
    const match = plain.match(CHECKBOX_LINE_RE);
    if (!match) return children !== b.children ? { ...b, children } : b;
    const markerStripped = (b.content || '').replace(
      /^(?:\s|&nbsp;|\u00a0)*(?:[-*]\s+)?\[(?: |x|-)?\](?:\s|&nbsp;|\u00a0)+/i,
      ''
    );
    return {
      ...b,
      type: 'checkbox',
      content: markerStripped,
      props: {
        ...b.props,
        ...tickToProps(checkboxTickFromMarker(match[1])),
      },
      children,
    };
  });

/**
 * HTML pastes often put markdown tables inside <blockquote>/<p> as literal
 * "| cell |" paragraphs. Collapse consecutive pipe-row text blocks into a
 * real table (works inside quotes/callouts too).
 */
const promotePipeTables = (blocks) => {
  const walk = (list) => {
    const out = [];
    let pipeRun = null; // { lines: string[], originals: Block[] }

    const flushPipe = () => {
      if (!pipeRun) return;
      const table = pipeLinesToTableBlock(pipeRun.lines);
      if (table) out.push(table);
      else out.push(...pipeRun.originals);
      pipeRun = null;
    };

    (list || []).forEach((b) => {
      const withKids = b.children?.length ? { ...b, children: walk(b.children) } : b;
      if (withKids.type === 'text') {
        const plain = stripHtml(withKids.content || '').replace(/\u00a0/g, ' ').trim();
        if (PIPE_ROW_RE.test(plain)) {
          pipeRun = pipeRun || { lines: [], originals: [] };
          pipeRun.lines.push(plain);
          pipeRun.originals.push(withKids);
          return;
        }
      }
      flushPipe();
      out.push(withKids);
    });
    flushPipe();
    return out;
  };
  return walk(blocks);
};

/**
 * Clipboard → block list, or null when the paste is plain inline content
 * that should just flow into the current block at the caret.
 *
 * Used by the editor-level paste handler: pasting a Notion section (heading,
 * paragraphs, bullets…) recreates the same STRUCTURE as separate blocks
 * instead of flattening everything into one text element.
 */
export const clipboardToBlocks = (html, text) => {
  let blocks = null;
  // Markdown-ish plain text (checkboxes, asides, headings…) beats the html
  // variant — when you copy from a chat/text editor, the html is usually
  // just that literal text in <p>/<div>s, and parsing it as html would keep
  // the `[ ]` / `#` markers as plain text instead of real blocks.
  if (textLooksLikeMarkdownStructure(text, html)) {
    blocks = textToBlocks(text);
  } else if (html && html.trim()) {
    try {
      blocks = promotePipeTables(
        promoteCheckboxMarkers(sanitizeBlockTree(htmlToBlocks(html)))
      );
    } catch {
      blocks = null;
    }
  } else if (text && text.includes('\n')) {
    blocks = textToBlocks(text);
  } else if (text && CHECKBOX_LINE_RE.test(text.trim())) {
    // Single-line checkbox with no HTML — still a structural paste
    blocks = textToBlocks(text);
  }
  // HTML→blocks can leave "| … |" rows as text inside quotes; promote those too
  // when the markdown text path wasn't used (or mixed trees slip through).
  if (blocks) blocks = promotePipeTables(blocks);
  // HTML pastes (and some text paths) leave literal "**bold**" in checkbox /
  // paragraph / table-cell strings — convert markers on the finished tree.
  if (blocks) blocks = applyInlineMarkdownToTree(blocks);
  if (!blocks || blocks.length === 0) return null;
  // NOTE: empty paragraphs are kept on purpose — an empty line in the source
  // is an empty line in the doc, exactly as if you'd hit Enter and moved on.
  // A single plain paragraph isn't a structural paste — inline it instead
  if (blocks.length === 1 && blocks[0].type === 'text') return null;
  return blocks;
};
